import { forwardRef, useState, type ButtonHTMLAttributes, type ImgHTMLAttributes, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { ChevronRight, Loader2 } from 'lucide-react';
import type { Condition } from '@shop/shared';
import { assetUrl } from '../lib/api';
import { Link, useFormat, useT } from '../lib/i18n';
import { useMoney } from '../lib/settings';

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------
type Variant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'light';
const sizes = { sm: 'h-9 px-4 text-[12px]', md: 'h-12 px-6', lg: 'h-14 px-8 text-[14px]' };

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: keyof typeof sizes;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, className = '', children, disabled, ...rest },
  ref,
) {
  return (
    <button ref={ref} disabled={disabled || loading} className={`btn btn-${variant} ${sizes[size]} ${className}`} {...rest}>
      {loading && <Loader2 className="absolute h-4 w-4 animate-spin" aria-hidden />}
      <span className={`inline-flex items-center gap-2 ${loading ? 'opacity-0' : ''}`}>{children}</span>
    </button>
  );
});

export function ButtonLink({ to, children, variant = 'primary', size = 'md', className = '' }: { to: string; children: ReactNode; variant?: Variant; size?: keyof typeof sizes; className?: string }) {
  const cls = `btn btn-${variant} ${sizes[size]} ${className}`;
  return /^https?:\/\//.test(to) ? (
    <a href={to} className={cls} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ) : (
    <Link to={to} className={cls}>
      {children}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Images: fade in once loaded, with responsive sources
// ---------------------------------------------------------------------------
interface ImgProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string | null | undefined;
  thumb?: string | null;
  sizes?: string;
  priority?: boolean;
}

export function Img({ src, thumb, sizes = '100vw', priority, className = '', alt = '', ...rest }: ImgProps) {
  const [loaded, setLoaded] = useState(false);
  if (!src) return <div className={`bg-sand ${className}`} aria-hidden />;
  const srcSet = thumb && thumb !== src ? `${assetUrl(thumb)} 720w, ${assetUrl(src)} 1600w` : undefined;
  return (
    <img
      src={assetUrl(src)}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={priority ? 'high' : undefined}
      onLoad={() => setLoaded(true)}
      className={`bg-sand transition-[opacity,transform,filter] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${loaded ? 'opacity-100' : 'opacity-0'} ${className}`}
      {...rest}
    />
  );
}

// ---------------------------------------------------------------------------
// Price
// ---------------------------------------------------------------------------
export function Price({ price, salePrice, priceMax, from, className = '' }: { price: number; salePrice?: number | null; priceMax?: number; from?: boolean; className?: string }) {
  const money = useMoney();
  const t = useT();
  const onSale = salePrice !== null && salePrice !== undefined && salePrice < price;
  const current = onSale ? salePrice! : price;
  const showFrom = from && priceMax !== undefined && priceMax > current;
  return (
    <span className={`inline-flex items-baseline gap-2 tabular-nums ${className}`}>
      {showFrom && <span className="text-stone">{t.common.from}</span>}
      <span className={onSale ? 'text-sale' : ''}>{money(current)}</span>
      {onSale && (
        <s className="text-stone/80 decoration-stone/60" aria-label={t.common.was(money(price))}>
          {money(price)}
        </s>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Second-hand details
// ---------------------------------------------------------------------------
export function Stamp({ children, tone = 'accent', className = '' }: { children: ReactNode; tone?: 'accent' | 'ink' | 'ok'; className?: string }) {
  const color = { accent: 'text-accent', ink: 'text-ink', ok: 'text-ok' }[tone];
  return <span className={`stamp ${color} ${className}`}>{children}</span>;
}

/** "1970s · Very good" in typewriter type. */
export function ItemFacts({ decade, condition, className = '' }: { decade: number | null; condition: Condition | null; className?: string }) {
  const f = useFormat();
  const parts = [decade ? f.decade(decade) : null, condition ? f.condition(condition) : null].filter(Boolean);
  if (!parts.length) return null;
  return <p className={`typewriter text-[13px] text-stone ${className}`}>{parts.join(' · ')}</p>;
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------
export function Reveal({ children, delay = 0, className = '', y = 20 }: { children: ReactNode; delay?: number; className?: string; y?: number }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-sand/80 ${className}`} aria-hidden />;
}

export function Breadcrumbs({ items }: { items: { label: string; to?: string }[] }) {
  const t = useT();
  return (
    <nav aria-label={t.common.breadcrumb} className="typewriter text-[12.5px] tracking-[0.04em] text-stone uppercase">
      <ol className="flex flex-wrap items-center gap-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="h-3 w-3 text-ash" aria-hidden />}
            {item.to ? (
              <Link to={item.to} className="link-underline hover:text-ink">
                {item.label}
              </Link>
            ) : (
              <span className="text-ink" aria-current="page">
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** A small printer's ornament between two hairlines. */
export function Ornament({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center gap-3 text-accent ${className}`} aria-hidden>
      <span className="h-px w-10 bg-current opacity-50" />
      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current">
        <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8z" />
      </svg>
      <span className="h-px w-10 bg-current opacity-50" />
    </div>
  );
}

export function SectionHeading({ eyebrow, title, action, center }: { eyebrow?: string; title: string; action?: ReactNode; center?: boolean }) {
  if (center) {
    return (
      <div className="mb-10 text-center md:mb-14">
        {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
        <h2 className="font-display text-[2.1rem] leading-[1.05] font-semibold text-balance md:text-[3rem]">{title}</h2>
        <Ornament className="mt-5" />
        {action && <div className="mt-5">{action}</div>}
      </div>
    );
  }
  return (
    <div className="mb-8 flex items-end justify-between gap-6 border-b border-ink/15 pb-5 md:mb-12">
      <div>
        {eyebrow && <p className="eyebrow mb-2.5">{eyebrow}</p>}
        <h2 className="font-display text-[2rem] leading-[1.05] font-semibold text-balance md:text-[2.75rem]">{title}</h2>
      </div>
      {action}
    </div>
  );
}

export function ErrorState({ title, message, onRetry }: { title?: string; message?: string; onRetry?: () => void }) {
  const t = useT();
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
      <p className="font-display text-2xl font-semibold">{title ?? t.common.somethingWrong}</p>
      {message && <p className="max-w-md text-stone">{message}</p>}
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {t.common.tryAgain}
        </Button>
      )}
    </div>
  );
}
